import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ShieldCheck, Search, Users, Activity, LogIn, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const FEATURES = [
  {
    icon: Users,
    title: 'Entitlements management',
    text: 'Access control across all OSDU environments',
  },
  {
    icon: Search,
    title: 'Data search',
    text: 'Advanced OSDU search across all environments',
  },
  {
    icon: Activity,
    title: 'Monitoring & analytics',
    text: 'Security monitoring, governance insights & data office analytics',
  },
];

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated, isLoading, error } = useAuth();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleLogin = async () => {
    setIsRedirecting(true);
    await login();
    setIsRedirecting(false);
  };

  // Already signed in — go straight to home (no render of the form, no flash).
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // While auth is still initializing, don't flash the login form.
  if (isLoading) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-10">
      <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white dark:bg-gray-800/80 shadow-sm dark:shadow-none dark:ring-1 dark:ring-gray-700/50">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {/* Left — Branding */}
          <div className="relative hidden md:flex flex-col justify-center gap-10 bg-gray-900 dark:bg-gray-950 p-12 text-white">
            <div>
              <img
                src="/logo.png"
                alt="Equinor"
                className="w-40 mb-8 brightness-0 invert"
              />
              <h1 className="text-3xl font-semibold tracking-tight">OSDU Operations</h1>
              <p className="mt-3 text-gray-300 leading-relaxed">
                Manage entitlements, search data, and monitor operations across all
                OSDU environments.
              </p>
            </div>

            <div className="space-y-5">
              {FEATURES.map(({ icon: Icon, title, text }) => (
                <div key={title} className="flex items-start gap-3.5">
                  <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{title}</p>
                    <p className="text-sm text-gray-400">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Sign in */}
          <div className="flex flex-col justify-center p-8 sm:p-12">
            <div className="mx-auto w-full max-w-sm">
              {/* Mobile logo */}
              <img
                src="/logo.png"
                alt="Equinor"
                className="mb-8 h-8 w-auto md:hidden dark:brightness-0 dark:invert"
              />

              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Welcome back
              </h2>
              <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                Sign in to access OSDU Operations
              </p>

              {error && (
                <div className="mt-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/50 dark:bg-red-900/20">
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={isRedirecting}
                className="mt-8 flex w-full items-center justify-center gap-2.5 rounded-xl bg-gray-900 px-6 py-3.5 font-medium text-white shadow-sm transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:disabled:bg-gray-600"
              >
                {isRedirecting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Redirecting to sign-in…
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5" />
                    Sign in with Equinor SSO
                  </>
                )}
              </button>

              <div className="mt-6 flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3.5 dark:border-gray-700/50 dark:bg-gray-800/60">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Secure Single Sign-On
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Use your Equinor credentials to access all environments securely.
                  </p>
                </div>
              </div>

              <p className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
                Protected by Equinor security protocols
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 